import { Link } from 'react-router-dom'
import { ShoppingBag, Users, ClipboardCheck, HeartHandshake, Camera, Download } from 'lucide-react'
import PageHero from '@/components/marketing/PageHero'

const HowItWorks = () => {
  const steps = [
    {
      icon: ShoppingBag,
      title: 'Book Your Package',
      description: 'Choose your photo/video package and secure your date. Choose add-ons right from your portal after you book.'
    },
    {
      icon: Users,
      title: 'We Match Your Photographer',
      description: 'Our team assigns a professional photographer/videographer based on your location, style, and availability.'
    },
    {
      icon: ClipboardCheck,
      title: 'Plan With Peace of Mind',
      description: 'Our coordinators gather timeline details, preferences, and questionnaires so your photographer shows up fully prepared.'
    },
    {
      icon: HeartHandshake,
      title: 'Meet Your Photographer',
      description: 'We introduce you to your assigned photographer about one month before the wedding to align on final details.'
    },
    {
      icon: Camera,
      title: 'Wedding Day Coverage',
      description: 'Your photographer captures all your key moments professionally and creatively.'
    },
    {
      icon: Download,
      title: 'Delivery & Download',
      description: 'Receive your beautifully edited gallery within your timeline. Download, share, and relive your day.'
    }
  ]

  const benefits = {
    clients: [
      'Access to vetted professional photographers',
      'Transparent pricing with no hidden fees',
      'Secure payment protection',
      'Real-time availability checking',
      'Direct communication with photographers',
      'Satisfaction guarantee'
    ],
    photographers: [
      'Reach thousands of potential clients',
      'Manage bookings efficiently',
      'Secure payment processing',
      'Build your professional portfolio',
      'Set your own rates and availability',
      'Focus on photography, not marketing'
    ]
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHero
        title="How Love & Photos Works"
        subtitle="A simple path to photographs you'll love."
      />

      {/* Process Steps */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-foreground mb-12">
            Your Journey to Perfect Photos
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <div key={index} className="relative">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-12 h-12 bg-primary-600 rounded-full">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {index + 1}. {step.title}
                      </h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-[60px] w-[calc(100%-60px)] h-[1px] bg-border" />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-foreground mb-12">
            Why Choose Love & Photos?
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Clients */}
            <div className="bg-background rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">For Clients</h3>
              <ul className="space-y-3">
                {benefits.clients.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/#negotiation-widget"
                className="mt-6 inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition"
              >
                Book Now
              </Link>
            </div>

            {/* For Photographers */}
            <div className="bg-background rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">For Photographers</h3>
              <ul className="space-y-3">
                {benefits.photographers.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/talent/apply"
                className="mt-6 inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition"
              >
                Apply to Join
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-6">Still Have Questions?</h2>
          <p className="text-muted-foreground mb-8">
            Check out our comprehensive FAQ section or get in touch with our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/faq" 
              className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              View FAQ
            </Link>
            <Link 
              to="/contact" 
              className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HowItWorks